import { useEffect } from 'react'

type Fbq = ((...args: unknown[]) => void) & {
  callMethod?: (...args: unknown[]) => void
  queue?: unknown[]
  push?: unknown
  loaded?: boolean
  version?: string
}

declare global {
  interface Window {
    fbq?: Fbq
    _fbq?: Fbq
  }
}

const SCRIPT_ID = 'meta-pixel-script'
const NOSCRIPT_ID = 'meta-pixel-noscript'
const SRC = 'https://connect.facebook.net/en_US/fbevents.js'

function ensureFbqStub(): Fbq | undefined {
  if (typeof window === 'undefined') return undefined
  if (window.fbq) return window.fbq

  const fbq = function (...args: unknown[]) {
    if (fbq.callMethod) {
      fbq.callMethod.apply(fbq, args)
    } else {
      fbq.queue?.push(args)
    }
  } as Fbq

  fbq.push = fbq
  fbq.loaded = true
  fbq.version = '2.0'
  fbq.queue = []

  window.fbq = fbq
  window._fbq = fbq
  return fbq
}

/**
 * Loads the Meta Pixel base code and fires the initial PageView.
 * Written defensively so a blocked/failed pixel can never break the page.
 */
export function useMetaPixel(pixelId: string) {
  useEffect(() => {
    if (typeof window === 'undefined' || typeof document === 'undefined') return

    try {
      const fbq = ensureFbqStub()
      if (!fbq) return

      if (!document.getElementById(SCRIPT_ID)) {
        const script = document.createElement('script')
        script.id = SCRIPT_ID
        script.async = true
        script.src = SRC
        const parent = document.head || document.documentElement
        if (parent) parent.appendChild(script)
      }

      fbq('init', pixelId)
      fbq('track', 'PageView')

      if (document.body && !document.getElementById(NOSCRIPT_ID)) {
        const noscript = document.createElement('noscript')
        noscript.id = NOSCRIPT_ID
        const img = document.createElement('img')
        img.height = 1
        img.width = 1
        img.style.display = 'none'
        img.alt = ''
        img.src = `https://www.facebook.com/tr?id=${pixelId}&ev=PageView&noscript=1`
        noscript.appendChild(img)
        document.body.appendChild(noscript)
      }
    } catch {
      /* the pixel must never take the page down */
    }
  }, [pixelId])
}

export function trackPixelEvent(event: string, params?: Record<string, unknown>) {
  if (typeof window === 'undefined') return
  try {
    window.fbq?.('track', event, params)
  } catch {
    /* no-op */
  }
}
